import type { MutationHookOptions, MutationTuple, QueryHookOptions } from '@apollo/react-hooks';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import type { QueryResult } from 'react-apollo';
import { defineMessages, useIntl } from 'react-intl-next';

import { isGraphQLError } from '@confluence/graphql/entry-points/isGraphQLError';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import { useFlags } from '../FlagsProvider';

const i18n = defineMessages({
	errorTitle: {
		id: 'questions.generic-error.title',
		defaultMessage: 'Something went wrong',
		description: 'The title for generic error message',
	},
	errorDescription: {
		id: 'questions.generic-error.description',
		defaultMessage: 'The request returned an error.',
		description: 'The description for generic error message',
	},
});

const extractErrors = (data: any, path: string): ApolloErrorInterop | null => {
	const errors = path.split('.').reduce((acc, part) => acc && acc[part], data);
	return errors && errors.length > 0 ? new ApolloErrorInterop({ graphQLErrors: errors }) : null;
};

type ErrorOptions = {
	errorTitle?: string;
	errorDescription?: string;
	responseErrorPath?: string;
};

const useErrorHandling = (errorOptions?: ErrorOptions) => {
	const { errorTitle, errorDescription } = errorOptions ?? {};
	const { showErrorFlag } = useFlags();
	const { formatMessage } = useIntl();

	//TODO apollo errors seems are not handled here
	return (error: ApolloErrorInterop) => {
		if (isGraphQLError(error) && !isErrorMarkedAsHandled(error)) {
			markErrorAsHandled(error);
		}

		void showErrorFlag({
			id: 'questions-error-flag',
			title: errorTitle || formatMessage(i18n.errorTitle),
			description: errorDescription || formatMessage(i18n.errorDescription),
			isAutoDismiss: true,
		});
	};
};

const handleCompletion = <TData, TVariables>(
	data: TData,
	errorOptions: ErrorOptions | undefined,
	options: MutationHookOptions<TData, TVariables> | undefined,
	handleError: (error: ApolloErrorInterop) => void,
) => {
	if (errorOptions?.responseErrorPath) {
		const error = extractErrors(data, errorOptions.responseErrorPath);

		if (error) {
			handleError(error);
			options?.onError && options.onError(error);
			return;
		}
	}

	options?.onCompleted && options.onCompleted(data);
};

export const useMutationWithError = <TData, TVariables>(
	mutation: any,
	options?: MutationHookOptions<TData, TVariables>,
	errorOptions?: ErrorOptions,
): MutationTuple<TData, TVariables> => {
	const handleError = useErrorHandling(errorOptions);

	const onCompleted = (data: TData) =>
		handleCompletion<TData, TVariables>(data, errorOptions, options, handleError);

	const onError = (error: ApolloErrorInterop) => {
		handleError(error);
		options?.onError && options.onError(error);
	};

	const [mutateFunction, result] = useMutation<TData, TVariables>(mutation, {
		...options,
		onCompleted,
		onError,
	});

	return [mutateFunction, result];
};

export const useQueryWithError = <TData, TVariables>(
	query: any,
	options?: QueryHookOptions<TData, TVariables>,
	errorOptions?: ErrorOptions,
): QueryResult<TData, TVariables> => {
	const handleError = useErrorHandling(errorOptions);

	const onCompleted = (data: TData) =>
		handleCompletion<TData, TVariables>(data, errorOptions, options, handleError);

	const onError = (error: ApolloErrorInterop) => {
		handleError(error);
		options?.onError && options.onError(error);
	};

	return useQuery<TData, TVariables>(query, {
		...options,
		onCompleted,
		onError,
	});
};
